import React, { useEffect, useRef, useState } from 'react'
import Button from '@mui/material/Button'
import { useDispatch, useSelector } from 'react-redux'
import Lock from '@mui/icons-material/Lock'
import InsertDriveFile from '@mui/icons-material/InsertDriveFile'
import Mustache from 'mustache'
import '@webscopeio/react-textarea-autocomplete/style.css'
import ReactTextareaAutocomplete from '@webscopeio/react-textarea-autocomplete'
import { Avatar, Box, Chip, Divider } from '@mui/material'

import { FileUpload, Loader, type FileWithType } from '~/shared/ui'
import { queueNotification } from '~/shared/notifications/actions'
import { patientName } from '~/entities/person'
import { useMacros, type TextMacro } from '~/entities/macro'
import { CaseCategory, useCases } from '~/entities/case'
import { ChatCaseSelection } from './ChatCaseSelection'
import { ChatThreadDescription, ChatThreadDescriptionHandle } from './ChatThreadDescription'
import { useManageSelectedTodo } from '../useManageSelectedTodo'
import { PatientUser } from '~/shared/types'
import { useLocalStorage } from '~/shared/hooks/useLocalStorage'
import { useMount, useUnmount } from '~/shared/hooks/useMount'
import {
  readClipboardForPerson,
  PatientMismatchError,
  TranscriptMessage,
} from './selection/clipboard'
import { MessageQuotePreview } from './selection'
import { useChat } from '../CommunicationThread'
import { logger } from '~/shared/lib/logger'
import { asString, isRecord, parseJson } from '~/shared/lib/guards'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const MacroItem = ({ entity: { name, template } }: { entity: TextMacro }) => (
  <div style={{ padding: 6, display: 'flex', maxWidth: 450, fontSize: 12 }}>
    <div
      style={{
        minWidth: 80,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        fontWeight: 500,
      }}
    >
      {name}
    </div>
    <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
      {template}
    </div>
  </div>
)
const MacroLoading = () => <div>Loading</div>

interface IChatInputProps {
  sendChat: (payload: {
    text: string
    file: FileWithType | null
    selectedCategory: CaseCategory | null
    focusedCase: { id: number; external_description?: string | null } | null
    externalDescription: string | undefined
    quotedMessages: TranscriptMessage[] | null
  }) => Promise<void>
  sending: boolean
  patient: Pick<PatientUser, 'id' | 'first_name'> & {
    person: Pick<PatientUser['person'], 'id' | 'preferred_name'>
  }
  showIntroductionReminder: boolean
  providerCanProvideClinicalCareToPatient: boolean
  threadUid: string | null
  isLocked?: boolean
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/tiff', 'image/jpg']
export const DOCUMENT_TYPES = ['application/pdf']

export const ChatInput: React.FC<IChatInputProps> = props => {
  const me = useSelector(state => state.me)
  const { search: searchMacros } = useMacros()
  const dispatch = useDispatch()
  const { selection } = useChat()
  const [text, setText] = useState('')
  // Store draft text in session storage. This helps guard against losing an
  // in-progress message due to user error (like accidentally refreshing the
  // screen) or programming error (like accidentally re-rendering the whole
  // component).
  //
  // Since session storage is ephemeral (as opposed to local storage) and still
  // subject to same-origin policy, we consider it no less secure than storing
  // the same text in the DOM.
  //
  // This is a JSON object including the patient ID so we don't mix up drafts
  // across different people.
  const [draftTextJSON, setDraftTextJSON] = useLocalStorage('chat.draft', null, { session: true })
  // Determine file type with FileWithType payload shape
  const [file, setFile] = useState<null | FileWithType>(null)
  const [quotedMessages, setQuotedMessages] = useState<TranscriptMessage[] | null>(null)
  const [uploadKey, setUploadKey] = useState<number>(0)
  const [selectedCategory, setSelectedCategory] = useState<CaseCategory | null>(null)
  // The component exposes its `<textarea>` as `textareaRef`, which its published types omit.
  const messagesInputRef = useRef<
    ReactTextareaAutocomplete<TextMacro> & { textareaRef?: HTMLTextAreaElement }
  >(null)
  const chatThreadDescriptionRef = useRef<ChatThreadDescriptionHandle>(null)

  // Restore and save draft text on mount and unmount.
  useMount(() => {
    if (!draftTextJSON) {
      return
    }

    // The stored draft is the patient-isolation check for restoring it, and its text goes on to be
    // sent as a message, so neither field is read through the parse's `any`.
    const stored = parseJson(draftTextJSON)
    if (!isRecord(stored) || stored.user_id !== props.patient.id) {
      return
    }

    const draft = asString(stored.draft)
    if (draft !== null) {
      setText(draft)
    }
  })
  useUnmount(() => {
    setDraftTextJSON(
      JSON.stringify({
        user_id: props.patient.id,
        draft: text,
      }),
    )
  })

  // Get URL state for focused case and payload
  const { selectedCaseId, selectPayload, resetToCase } = useManageSelectedTodo()

  // A quoteMessage payload in the URL fills the quote preview, then is cleared from the URL.
  const isQuotePayload =
    selectPayload?.type === 'quoteMessage' && selectPayload.quotedMessages?.length > 0
  const [consumedPayload, setConsumedPayload] = useState<typeof selectPayload>()
  if (isQuotePayload && selectPayload !== consumedPayload) {
    setConsumedPayload(selectPayload)
    setQuotedMessages(selectPayload.quotedMessages)
  }
  useEffect(() => {
    if (isQuotePayload && selectedCaseId !== null) resetToCase(selectedCaseId)
  }, [isQuotePayload, selectedCaseId, resetToCase])

  // Fetch cases for the patient
  const { data: patientCases = [] } = useCases({ personId: props.patient.person.id })

  const { sending } = props

  // Determine the effective case or category for sending messages
  const focusedCase = selectedCaseId
    ? (patientCases.find(c => c.id === selectedCaseId) ?? null)
    : null
  const effectiveCaseOrCategory = focusedCase ?? selectedCategory

  const resizeInput = () => {
    setTimeout(() => {
      const textarea = messagesInputRef.current?.textareaRef
      if (!textarea) return

      const offset = textarea.offsetHeight - textarea.clientHeight
      textarea.style.height = 'auto'
      textarea.style.height = `${(textarea.scrollHeight + offset) / 10}rem`
    }, 0)
  }

  // Sizes the textarea once on mount.
  useMount(resizeInput)

  /**
   * Consider the external description generation UI enabled if:
   * 1. A CaseCategory is selected (the user is creating a new Case)
   * 2. A Case is selected and is missing an external_description
   */
  const isCasingOutboundDescriptionEnabled =
    selectedCategory !== null || (focusedCase && !focusedCase.external_description)

  const handleSendChat = async () => {
    const { sendChat } = props
    const input = text.trim()
    if (input.length || file || quotedMessages?.length) {
      // Get external description if needed
      let externalDescription: string | undefined = undefined
      if (isCasingOutboundDescriptionEnabled) {
        externalDescription = chatThreadDescriptionRef.current?.getDescription()
        // Check if description is ready. If not, show feedback
        if (!externalDescription) {
          dispatch(
            queueNotification({
              variant: 'info',
              message: 'Please wait for the thread title to generate or add a custom one',
            }),
          )
          return
        }
      }

      await sendChat({
        text: input,
        file,
        selectedCategory,
        focusedCase,
        externalDescription,
        quotedMessages,
      })

      // Reset local input state on success
      setText('')
      setQuotedMessages(null)
      setDraftTextJSON(null)
      setSelectedCategory(null)
      messagesInputRef.current?.textareaRef?.focus()
      handleRemoveAttachment()
    }
  }

  // Clear file input by updating the render key
  const handleRemoveAttachment = () => {
    setFile(null)
    setUploadKey(current => current + 1)
  }

  // Handle paste event for quoted messages
  const handlePaste = async (e: React.ClipboardEvent) => {
    // Check if HTML clipboard data contains our custom data attribute
    const html = e.clipboardData.getData('text/html')
    if (!html.includes('data-firefly-chat-selection')) {
      // Not our custom format, let default paste behavior continue
      return
    }

    // Prevent default paste immediately since we know this is our format
    e.preventDefault()

    // Now parse and validate asynchronously
    try {
      const messages = await readClipboardForPerson(props.patient.person.id)
      if (messages) {
        setQuotedMessages(messages)
        // Clear message selection after successful paste
        selection.clearSelection()
      }
    } catch (err) {
      let errorMessage = 'Failed to paste messages'
      if (err instanceof PatientMismatchError) {
        errorMessage = 'Cannot paste messages from a different patient'
      }
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        errorMessage = 'Clipboard access denied. Please allow clipboard access to paste messages.'
      }
      dispatch(queueNotification({ variant: 'error', message: errorMessage }))
      logger.error(errorMessage, err)
    }
  }

  const handleSelectCategory = (newCategory: CaseCategory | null) => {
    setSelectedCategory(newCategory)

    messagesInputRef.current?.textareaRef?.focus()
  }

  const { showIntroductionReminder, providerCanProvideClinicalCareToPatient } = props
  const derivedName = patientName(props.patient)

  // Override placeholder and input text based on context
  let inputPlaceholder = 'Message'
  if (!effectiveCaseOrCategory) inputPlaceholder = 'Add or edit case to start messaging'
  if (showIntroductionReminder && providerCanProvideClinicalCareToPatient) {
    inputPlaceholder = `Introduce yourself to ${derivedName}`
  }
  if (props.isLocked) inputPlaceholder = 'Conversation locked'

  const inputDisabled = sending || props.isLocked

  // Add the `name` property for use in Macros
  const patient = { name: derivedName, ...props.patient }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(sending ? { pointerEvents: 'none', opacity: 0.5 } : {}),
      }}
    >
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '& .rta__textarea': {
            bgcolor: 'background.default',
            color: 'grey.600',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            resize: 'none',
            fontFamily: 'inherit',
            display: 'block',
            padding: '0.75em',
            '&:focus': { borderBottom: '2px solid', borderBottomColor: 'primary.main' },
          },
        }}
      >
        {isCasingOutboundDescriptionEnabled && (
          <ChatThreadDescription
            ref={chatThreadDescriptionRef}
            selectedCategory={selectedCategory}
            focusedCase={focusedCase}
            message={text}
          />
        )}
        {quotedMessages ? (
          <MessageQuotePreview messages={quotedMessages} onRemove={() => setQuotedMessages(null)} />
        ) : null}
        <ReactTextareaAutocomplete<TextMacro>
          ref={messagesInputRef}
          value={text}
          disabled={inputDisabled}
          onChange={e => {
            setText(e.target.value)
            resizeInput()
          }}
          rows={1}
          placeholder={inputPlaceholder}
          onPaste={e => void handlePaste(e)}
          renderToBody={true}
          loadingComponent={MacroLoading}
          dropdownStyle={{ width: 250 }}
          trigger={{
            '.': {
              dataProvider: searchMacros,
              component: MacroItem,
              output: item => {
                // Templates are free text edited in Settings, and this runs for
                // every dropdown item — with single-brace delimiters, one stray
                // `{` in one macro would otherwise throw and take down the whole
                // composer. Fall back to the raw template: still usable, and the
                // typo stays visible to whoever can fix the macro.
                try {
                  return Mustache.render(
                    item.template,
                    {
                      me: { firstName: me.first_name, lastName: me.last_name },
                      patient,
                    },
                    {},
                    ['{', '}'],
                  )
                } catch (error) {
                  // The Error goes as its own context arg: the logger's
                  // instanceof-Error serialisation (stack included) applies per
                  // argument, and an Error nested in a plain object would
                  // collapse to {} in the JSON pass.
                  logger.error('Macro template failed to render', { macro: item.name }, error)
                  return item.template
                }
              },
            },
          }}
        />
        {file?.data.name ? (
          <Box sx={{ mx: 2, mb: 0 }}>
            <Chip
              avatar={
                file.type === 'image' ? (
                  <Avatar src={URL.createObjectURL(file.data)} alt={file.data.name} />
                ) : undefined
              }
              icon={file.type === 'image' ? undefined : <InsertDriveFile fontSize="small" />}
              onDelete={() => setFile(null)}
              label={file.data.name}
            />
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            px: 1,
            pt: 0,
            pb: 0.5,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ChatCaseSelection
              person={{
                id: props.patient.person.id,
                user: {
                  id: props.patient.id,
                },
              }}
              selectedCategory={selectedCategory}
              onCategorySelect={handleSelectCategory}
              disabled={sending}
              threadUid={props.threadUid}
            />
          </Box>
          {/**
           * Disable attaching files when starting a new thread
           */}
          <FileUpload
            disabled={sending || !effectiveCaseOrCategory}
            file={file}
            key={uploadKey}
            handleFile={setFile}
            supportedImageTypes={IMAGE_TYPES}
            supportedDocTypes={DOCUMENT_TYPES}
            supportMessage="Supports JPG, PNG, TIFF, or PDF"
          />
          <Button
            variant="contained"
            color="primary"
            disabled={
              (text.trim().length === 0 && !file && !quotedMessages?.length) ||
              sending ||
              !effectiveCaseOrCategory ||
              props.isLocked
            }
            onClick={() => fireAndForget('[Chat] send message', handleSendChat)}
            startIcon={props.isLocked ? <Lock /> : undefined}
          >
            {sending ? <Loader inline /> : 'Send'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
